import { confirmDialog, notify, reportLoadError } from '../utils/notify';
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api, { settingsAPI } from '../services/api';
import { useLanguage } from '../contexts/LanguageContext';
import LanguageSwitcher from '../components/LanguageSwitcher';
import OvertimeTaxFrozen from '../components/OvertimeTaxFrozen';

import { Building2, Calculator, Clock, FlaskConical, Globe, Landmark, Save, Wallet } from 'lucide-react';
import './Settings.css';

const Settings = () => {
  const { t, locale } = useLanguage();
  const [settingsData, setSettingsData] = useState({});
  const [loading, setLoading] = useState(false);

  // ========== PAYROLL SETTINGS (payroll_settings, what payroll reads) ==========
  const [payrollConfig, setPayrollConfig] = useState(null);
  const loadPayrollConfig = async () => {
    try {
      const response = await settingsAPI.getPayrollSettings();
      setPayrollConfig(response.data.config);
    } catch (error) {
      console.error('Error loading payroll settings:', error);
      reportLoadError(error, t('Error loading payroll settings'));
    }
  };
  const updatePayrollField = (field, value) => setPayrollConfig(prev => ({ ...prev, [field]: value }));
  // The same ranges as the database and the server.
  const PAYROLL_RANGES = {
    sunday_rate_default: [1, 5, 'Sunday Rate Multiplier'], holiday_rate_default: [1, 5, 'Holiday Rate Multiplier'],
    working_days_per_month: [1, 31, 'Working Days Per Month'], standard_hours_per_day: [0, 24, 'Hours per Day'],
    sick_pay_percentage: [0, 100, 'Sick Pay (%)']
  };
  const payrollConfigProblem = () => Object.entries(PAYROLL_RANGES).find(([key, [min, max]]) => {
    const v = payrollConfig[key];
    const n = Number(v);
    return v === '' || !Number.isFinite(n) || n < min || n > max
      || (key === 'standard_hours_per_day' && n <= 0)
      || (key === 'working_days_per_month' && !Number.isInteger(n));
  });
  const savePayrollConfig = async () => {
    const problem = payrollConfigProblem();
    if (problem) {
      notify(t('Check {field}: it must be between {min} and {max}', { field: t(problem[1][2]), min: problem[1][0], max: problem[1][1] }), 'error');
      return;
    }
    try {
      setLoading(true);
      const response = await settingsAPI.updatePayrollSettings({
        ...payrollConfig,
        sunday_rate_default: Number(payrollConfig.sunday_rate_default),
        holiday_rate_default: Number(payrollConfig.holiday_rate_default),
        working_days_per_month: Number(payrollConfig.working_days_per_month),
        standard_hours_per_day: Number(payrollConfig.standard_hours_per_day),
        sick_pay_percentage: Number(payrollConfig.sick_pay_percentage)
      });
      setPayrollConfig(response.data.config);
      notify(t('Payroll settings saved'), 'success');
    } catch (error) {
      console.error('Error saving payroll settings:', error);
      notify(error.response?.data?.message || t('Failed to save'), 'error');
    } finally {
      setLoading(false);
    }
  };
const [overtimeTestGross, setOvertimeTestGross] = useState('976.56');
const [overtimeTestResult, setOvertimeTestResult] = useState(null);

const testOvertimeTaxCalc = async () => {
  try {
    if (!overtimeTestGross || parseFloat(overtimeTestGross) <= 0) {
      notify(t('Please enter a valid overtime gross amount'), 'error');
      return;
    }
    
    setLoading(true);
    const response = await api.get('/settings/overtime-tax/test', {
      params: { overtimeGross: overtimeTestGross }
    });
    
    if (response.data.success) {
      setOvertimeTestResult(response.data.calculation);
    }
  } catch (error) {
    console.error('Error testing overtime tax:', error);
    notify(t('Error: {message}', { message: error.response?.data?.message || t('Failed to test calculation') }));
  } finally {
    setLoading(false);
  }
};

  // ========== LOAD DATA ==========
  useEffect(() => {
    loadSettings();
    loadPayrollConfig();
  }, []);

  const loadSettings = async () => {
    try {
      setLoading(true);
      const response = await settingsAPI.getAll();
      
      // Convert array to object { key: value }
      const settingsObj = {};
      response.data.settings.forEach(setting => {
        settingsObj[setting.setting_key] = setting.setting_value;
      });
      // Guardar session timeout en localStorage para AuthContext
        if (settingsObj.session_timeout_minutes) {
          localStorage.setItem('session_timeout_minutes', settingsObj.session_timeout_minutes);
        }
      
      setSettingsData(settingsObj);
    } catch (error) {
      console.error('Error loading settings:', error);
      reportLoadError(error, t('Error loading settings'), { main: true });
    } finally {
      setLoading(false);
    }
  };

  // ========== GENERAL SETTINGS FUNCTIONS ==========
  const handleSave = async (e) => {
  e.preventDefault();
  try {
    setLoading(true);
    // bulkUpdate takes { key: value } (it builds the list itself).
    const response = await settingsAPI.bulkUpdate(
      Object.fromEntries(Object.entries(settingsData).map(([key, value]) => [key, String(value)]))
    );
    // Actualizar localStorage con el nuevo timeout
    if (settingsData.session_timeout_minutes) {
      localStorage.setItem('session_timeout_minutes', settingsData.session_timeout_minutes);
    }
    const skipped = response.data.skipped || [];
    if (skipped.length > 0) {
      notify(t('Saved, except settings that do not exist: {keys}', { keys: skipped.join(', ') }), 'warning');
    } else {
      notify(t('Settings saved successfully!'), 'success');
    }
  } catch (error) {
    notify(error.response?.data?.message || t('Error saving settings'), 'error');
  } finally {
    setLoading(false);
  }
};
  const updateSetting = (key, value) => {
    setSettingsData(prev => ({ ...prev, [key]: value }));
  };

  return (
    <div className="settings-page">
      <div className="page-head">
        <div>
          <h1 className="page-title">{t('System Settings')}</h1>
          <p className="page-sub">{t('Configure system-wide settings')}</p>
        </div>
      </div>

      <form onSubmit={handleSave} className="set-form">
        <div className="card set-section">
          <h2 className="set-section__title"><Globe aria-hidden="true" /> {t('Language')}</h2>
          <p className="set-lead">{t('Choose your preferred language')}</p>
          <LanguageSwitcher compact />
        </div>
        {/* Company Information */}
        <div className="card set-section">
          <h2 className="set-section__title"><Building2 aria-hidden="true" /> {t('Company Information')}</h2>
          <div className="set-form-row">
            <div className="set-field">
              <label className="field-label">{t('Company Name')} *</label>
              <input className="input"
                type="text"
                value={settingsData.company_name || ''}
                onChange={(e) => updateSetting('company_name', e.target.value)}
                required
              />
            </div>
            <div className="set-field">
              <label className="field-label">{t('Email')} *</label>
              <input className="input"
                type="email"
                value={settingsData.company_email || ''}
                onChange={(e) => updateSetting('company_email', e.target.value)}
                required
              />
            </div>
            <div className="set-field">
              <label className="field-label">{t('Phone')} *</label>
              <input className="input"
                type="tel"
                value={settingsData.company_phone || ''}
                onChange={(e) => updateSetting('company_phone', e.target.value)}
                required
              />
            </div>
            <div className="set-field set-field--wide">
              <label className="field-label">{t('Address')}</label>
              <input className="input"
                type="text"
                value={settingsData.company_address || ''}
                onChange={(e) => updateSetting('company_address', e.target.value)}
              />
            </div>
          </div>
        </div>

        {/* Loonbelasting is in Tax Configuration, with the brackets and exemptions of each year */}
        <div className="card set-section">
          <h2 className="set-section__title"><Landmark aria-hidden="true" /> {t('Loonbelasting Configuration (Suriname Wage Tax)')}</h2>
          <p className="set-lead">{t('Loonbelasting is set per year in Tax Configuration, with its brackets and exemptions.')}</p>
          <Link to="/tax-configuration">{t('Open Tax Configuration')}</Link>
        </div>
        {/* ========== OVERTIME TAX CONFIGURATION ========== */}
<div className="card set-section set-overtime">
  {/* Frozen until the client decides: what payroll applies, read-only */}
  <h2 className="set-section__title"><Clock aria-hidden="true" /> {t('Overtime Tax Configuration (Belasting Overuren)')}</h2>
  <OvertimeTaxFrozen />

  {/* Test Calculator */}
  <div className="set-test">
    <h3 className="set-test__title"><FlaskConical aria-hidden="true" /> {t('Test Overtime Tax Calculator')}</h3>
    <p>{t('Test the overtime tax calculation with a sample gross amount')}</p>
    
    <div className="set-test__input">
      <div className="set-field">
        <label className="field-label">{t('Overtime Gross (SRD)')}</label>
        <input className="input"
          type="number"
          value={overtimeTestGross}
          onChange={(e) => setOvertimeTestGross(e.target.value)}
          step="0.01"
          min="0"
          placeholder={t('Enter overtime gross (e.g., 976.56)')}
        />
      </div>
      <button 
        type="button"
        onClick={testOvertimeTaxCalc} 
        className="btn btn--primary"
        disabled={loading || !overtimeTestGross}
      >
        {loading ? t('Calculating...') : <><Calculator aria-hidden="true" /> {t('Calculate')}</>}
      </button>
    </div>

    {/* Test Result */}
    {overtimeTestResult && (
      <div className="set-result">
        <h4>{t('Calculation Result for SRD {amount}', { amount: parseFloat(overtimeTestGross).toLocaleString(locale) })}</h4>
        
        <div className="set-result__table">
          <div className="set-result__row">
            <span className="set-result__label">{t('Overtime Gross')}:</span>
            <span className="set-result__value is-strong">SRD {overtimeTestResult.overtimeGross.toLocaleString(locale)}</span>
          </div>
          
          <div className="set-result__row is-tax">
            <span className="set-result__label">{t('Overtime Tax ({bracket}):', { bracket: overtimeTestResult.bracket })}</span>
            <span className="set-result__value is-tax">-SRD {overtimeTestResult.overtimeTax.toLocaleString(locale)}</span>
          </div>
          
          <div className="set-result__divider"></div>
          
          <div className="set-result__row is-net">
            <span className="set-result__label">{t('Overtime Net')}:</span>
            <span className="set-result__value is-net">SRD {overtimeTestResult.overtimeNet.toLocaleString(locale)}</span>
          </div>
          
          <div className="set-result__row is-info">
            <span className="set-result__label">{t('Effective Tax Rate')}:</span>
            <span className="set-result__value">{overtimeTestResult.effectiveRate}%</span>
          </div>
        </div>
      </div>
    )}
  </div>
</div>

        {/* ========== PAYROLL SETTINGS (payroll_settings) ========== */}
        <div className="card set-section">
          <h2 className="set-section__title"><Wallet aria-hidden="true" /> {t('Payroll Settings')}</h2>
          <p className="set-lead">{t('Payroll reads these values. Saving applies them to the next calculation.')}</p>
          {!payrollConfig ? (
            <p className="set-lead">{t('Loading...')}</p>
          ) : (
          <div className="set-form-row">
            <div className="set-field set-field--wide">
              <label className="set-check">
                <input
                  type="checkbox"
                  checked={payrollConfig.enable_sunday_premium}
                  onChange={(e) => updatePayrollField('enable_sunday_premium', e.target.checked)}
                />
                <span>{t('Enable Sunday Premium Pay')}</span>
              </label>
            </div>
            <div className="set-field">
              <label htmlFor="ps-sunday">{t('Sunday Rate Multiplier')} *</label>
              <input className="input" id="ps-sunday" type="number" step="0.1" min="1" max="5"
                value={payrollConfig.sunday_rate_default}
                onChange={(e) => updatePayrollField('sunday_rate_default', e.target.value)}
                disabled={!payrollConfig.enable_sunday_premium} />
              <small className="field-hint">{t('From 1 to 5 (1.5 = 150% of the hourly rate)')}</small>
            </div>
            <div className="set-field set-field--wide">
              <label className="set-check">
                <input
                  type="checkbox"
                  checked={payrollConfig.enable_holiday_premium}
                  onChange={(e) => updatePayrollField('enable_holiday_premium', e.target.checked)}
                />
                <span>{t('Enable Holiday Premium Pay')}</span>
              </label>
            </div>
            <div className="set-field">
              <label htmlFor="ps-holiday">{t('Holiday Rate Multiplier')} *</label>
              <input className="input" id="ps-holiday" type="number" step="0.1" min="1" max="5"
                value={payrollConfig.holiday_rate_default}
                onChange={(e) => updatePayrollField('holiday_rate_default', e.target.value)}
                disabled={!payrollConfig.enable_holiday_premium} />
              <small className="field-hint">{t('From 1 to 5 (2.0 = 200% of the hourly rate)')}</small>
            </div>
            <div className="set-field set-field--wide">
              <small className="field-hint">{t('Note: these rates apply only to employees who do not have their own multiplier set on their profile. An employee-level value always takes precedence over this default.')}</small>
            </div>
            <div className="set-field">
              <label htmlFor="ps-days">{t('Working Days Per Month')} *</label>
              <input className="input" id="ps-days" type="number" step="1" min="1" max="31"
                value={payrollConfig.working_days_per_month}
                onChange={(e) => updatePayrollField('working_days_per_month', e.target.value)} />
              <small className="field-hint">{t('Used to prorate monthly salaries (1 to 31)')}</small>
            </div>
            <div className="set-field">
              <label htmlFor="ps-hours">{t('Hours per Day')} *</label>
              <input className="input" id="ps-hours" type="number" step="0.5" min="0.5" max="24"
                value={payrollConfig.standard_hours_per_day}
                onChange={(e) => updatePayrollField('standard_hours_per_day', e.target.value)} />
              <small className="field-hint">{t('A working day for daily rates and paid leave (up to 24)')}</small>
            </div>
            <div className="set-field">
              <label htmlFor="ps-sick">{t('Sick Pay (%)')} *</label>
              <input className="input" id="ps-sick" type="number" step="1" min="0" max="100"
                value={payrollConfig.sick_pay_percentage}
                onChange={(e) => updatePayrollField('sick_pay_percentage', e.target.value)} />
              <small className="field-hint">{t('Share of the pay for a sick day (0 to 100)')}</small>
            </div>
            <div className="set-field set-field--wide">
              <button type="button" onClick={savePayrollConfig} className="btn btn--primary" disabled={loading}>
                {loading ? t('Saving...') : <><Save aria-hidden="true" /> {t('Save Payroll Settings')}</>}
              </button>
            </div>
          </div>
          )}
        </div>

        <div className="set-form-actions">
          <button type="submit" className="btn btn--primary" disabled={loading}>
            {loading ? t('Saving...') : <><Save aria-hidden="true" /> {t('Save General Settings')}</>}
          </button>
        </div>
      </form>
    </div>
  );
};

export default Settings;